import { useEffect, useState } from "react";
import { api } from "../../api";
import { Combobox } from "../../components/ui/combobox";
import { useLanguage } from "../../contexts/LanguageContext";
import { metricsLabels } from "./labels";
import { MetricsSummaryCards } from "./components/MetricsSummaryCards";
import { AppointmentsChart } from "./components/AppointmentsChart";
import { PlanStatsChart } from "./components/PlanStatsChart";

export default function ConsultasPorDiaPage() {
  const { lang } = useLanguage();
  const t = metricsLabels[lang];
  const [data, setData] = useState<{ day: string; count: number; revenue: number; parts: any[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<{ id: number; name: string; color?: string | null }[]>([]);
  const [employeeId, setEmployeeId] = useState<string>("all");
  const [period, setPeriod] = useState<string>("month");
  const [prevTotal, setPrevTotal] = useState<number | null>(null);
  const [prevRevenue, setPrevRevenue] = useState<number | null>(null);
  const [hover, setHover] = useState<null | { idx: number; name: string; count: number; revenue: number; x: number; y: number }>(null);
  const [planStats, setPlanStats] = useState<{
    active_plans: { count: number; total_cents: number };
    late_payments: { count: number; total_cents: number };
    paid_payments: { count: number; total_cents: number };
  } | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  function fmtDateLocal(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function parseYmd(s: string) {
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, (m - 1), d);
    dt.setHours(0, 0, 0, 0);
    return dt;
  }

  function startOfISOWeek(d: Date) {
    const dt = new Date(d);
    const day = dt.getDay();
    const iso = day === 0 ? 7 : day;
    dt.setDate(dt.getDate() - (iso - 1));
    dt.setHours(0, 0, 0, 0);
    return dt;
  }

  function shouldGroupWeeks(p: string) {
    return p === "month" || p === "30d" || p === "90d" || p === "180d" || p === "365d";
  }

  function periodToDays(p: string) {
    switch (p) {
      case "7d": return 7;
      case "15d": return 15;
      case "30d": return 30;
      case "90d": return 90;
      case "180d": return 180;
      case "365d": return 365;
      default: return 30;
    }
  }

  useEffect(() => {
    api.listEmployees().then(res => { if (res.ok) setEmployees(res.data); });
  }, []);

  const load = async () => {
    setLoading(true);
    const end = new Date();
    let start = new Date(end);
    if (period === "month") {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else {
      const days = periodToDays(period);
      start.setDate(end.getDate() - (days - 1));
    }
    const res = await api.appointmentsPerDay({
      start: fmtDateLocal(start),
      end: fmtDateLocal(end),
      employee_id: employeeId !== "all" ? Number(employeeId) : undefined,
      breakdown: true,
    });
    if (res.ok) {
      const rows = res.data.map((r: any) => ({
        day: typeof r.day === "string" ? r.day.slice(0, 10) : String(r.day),
        employee_id: r.employee_id != null ? Number(r.employee_id) : undefined,
        count: Number(r.count) || 0,
        revenue_cents: Number(r.revenue_cents) || 0,
      }));
      const dayMap = new Map<string, Map<number, { count: number; revenue: number }>>();
      for (const r of rows) {
        const d = r.day;
        const eid = r.employee_id ?? -1;
        if (!dayMap.has(d)) dayMap.set(d, new Map());
        const m = dayMap.get(d)!;
        const current = m.get(eid) || { count: 0, revenue: 0 };
        m.set(eid, { count: current.count + r.count, revenue: current.revenue + r.revenue_cents });
      }
      const dayKeys: string[] = [];
      const cursor = new Date(start);
      const endInclusive = new Date(end);
      while (cursor <= endInclusive) {
        const key = fmtDateLocal(cursor);
        dayKeys.push(key);
        if (!dayMap.has(key)) dayMap.set(key, new Map());
        cursor.setDate(cursor.getDate() + 1);
      }
      let grouped: Map<string, Map<number, { count: number; revenue: number }>>;
      if (shouldGroupWeeks(period)) {
        grouped = new Map();
        for (const key of dayKeys) {
          const wk = fmtDateLocal(startOfISOWeek(parseYmd(key)));
          if (!grouped.has(wk)) grouped.set(wk, new Map());
          const m = grouped.get(wk)!;
          const dm = dayMap.get(key)!;
          for (const [eid, vals] of dm.entries()) {
            const current = m.get(eid) || { count: 0, revenue: 0 };
            m.set(eid, { count: current.count + vals.count, revenue: current.revenue + vals.revenue });
          }
        }
      } else {
        grouped = new Map(dayMap);
      }
      const xKeys = Array.from(grouped.keys()).sort();
      const employeeOrder = employees.map(e => e.id);
      const series = xKeys.map(k => {
        const valsMap = grouped.get(k)!;
        const parts: { id: number; count: number; revenue: number }[] = [];
        const presentIds = new Set(valsMap.keys());
        for (const id of employeeOrder) if (valsMap.has(id)) {
          const v = valsMap.get(id)!;
          parts.push({ id, count: v.count, revenue: v.revenue });
        }
        for (const id of presentIds) if (!employeeOrder.includes(id)) {
          const v = valsMap.get(id)!;
          parts.push({ id, count: v.count, revenue: v.revenue });
        }
        const total = parts.reduce((s, p) => s + p.count, 0);
        const revenue = parts.reduce((s, p) => s + p.revenue, 0);
        return { day: k, total, revenue, parts };
      });
      setData(series);
    }
    let prevStart: Date;
    let prevEnd: Date;
    if (period === "month") {
      prevStart = new Date(end.getFullYear(), end.getMonth() - 1, 1);
      prevEnd = new Date(end.getFullYear(), end.getMonth(), 0);
    } else {
      const days = periodToDays(period);
      prevEnd = new Date(start);
      prevEnd.setDate(prevEnd.getDate() - 1);
      prevStart = new Date(prevEnd);
      prevStart.setDate(prevStart.getDate() - (days - 1));
    }
    const prevRes = await api.appointmentsPerDay({
      start: fmtDateLocal(prevStart),
      end: fmtDateLocal(prevEnd),
      employee_id: employeeId !== "all" ? Number(employeeId) : undefined,
      breakdown: true,
    });
    if (prevRes.ok) {
      const totalPrev = prevRes.data.reduce((sum: number, r: any) => sum + Number(r.count || 0), 0);
      const revenuePrev = prevRes.data.reduce((sum: number, r: any) => sum + Number(r.revenue_cents || 0), 0);
      setPrevTotal(totalPrev);
      setPrevRevenue(revenuePrev);
    } else {
      setPrevTotal(null);
      setPrevRevenue(null);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [employeeId, period, employees]);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const end = new Date();
      let start = new Date(end);
      if (period === "month") {
        start = new Date(end.getFullYear(), end.getMonth(), 1);
      } else {
        const days = periodToDays(period);
        start.setDate(end.getDate() - (days - 1));
      }
      const res = await api.plansAndPayments({
        start: fmtDateLocal(start),
        end: fmtDateLocal(end),
      });
      if (res.ok) setPlanStats(res.data);
      setLoadingPlans(false);
    };
    fetchPlans();
  }, [period]);

  const total = data.reduce((sum, d: any) => sum + (d.total ?? d.count ?? 0), 0);
  const totalRevenue = data.reduce((sum, d: any) => sum + (d.revenue ?? 0), 0);
  const max = Math.max(1, ...data.map((d: any) => (d.total ?? d.count ?? 0)));
  const yStep = Math.max(1, Math.ceil(max / 4));
  const yMax = Math.max(yStep, yStep * Math.ceil(max / yStep));
  const yTicks = [0, yStep, yStep * 2, yStep * 3, yMax];
  
  const hasPrev = prevTotal !== null;
  const diff = hasPrev ? total - (prevTotal as number) : 0;
  const diffPct = hasPrev && (prevTotal as number) !== 0 ? (diff / (prevTotal as number)) * 100 : 0;

  const hasPrevRevenue = prevRevenue !== null;
  const diffRevenue = hasPrevRevenue ? totalRevenue - (prevRevenue as number) : 0;
  const diffPctRevenue = hasPrevRevenue && (prevRevenue as number) !== 0 ? (diffRevenue / (prevRevenue as number)) * 100 : 0;

  const planItems = planStats
    ? [
        { label: t.dashboard.plans.active, count: planStats.active_plans.count, total: planStats.active_plans.total_cents, color: "var(--chart-1, #6d28d9)" },
        { label: t.dashboard.plans.late, count: planStats.late_payments.count, total: planStats.late_payments.total_cents, color: "var(--chart-2, #e11d48)" },
        { label: t.dashboard.plans.paid, count: planStats.paid_payments.count, total: planStats.paid_payments.total_cents, color: "var(--chart-3, #16a34a)" },
      ]
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Combobox
          options={[
            { value: "all", label: t.filters.allProfessionals },
            ...employees.map((e) => ({ value: String(e.id), label: e.name })),
          ]}
          value={employeeId}
          onChange={setEmployeeId}
          placeholder={t.filters.professionalPlaceholder}
          searchPlaceholder={t.filters.searchPlaceholder}
          emptyMessage={t.filters.emptyMessage}
          ariaLabel={t.filters.professionalPlaceholder}
          className="w-56"
        />
        <Combobox
          options={[
            { value: "month", label: t.filters.period.month },
            { value: "7d", label: t.filters.period.last7 },
            { value: "15d", label: t.filters.period.last15 },
            { value: "30d", label: t.filters.period.last30 },
            { value: "90d", label: t.filters.period.last90 },
            { value: "180d", label: t.filters.period.last180 },
            { value: "365d", label: t.filters.period.last365 },
          ]}
          value={period}
          onChange={setPeriod}
          placeholder={t.filters.periodPlaceholder}
          searchPlaceholder={t.filters.searchPlaceholder}
          emptyMessage={t.filters.emptyMessage}
          ariaLabel={t.filters.periodPlaceholder}
          className="w-40"
        />
      </div>

      <MetricsSummaryCards
        lang={lang}
        t={t}
        total={total}
        totalRevenue={totalRevenue}
        hasPrev={hasPrev}
        diff={diff}
        diffPct={diffPct}
        hasPrevRevenue={hasPrevRevenue}
        diffRevenue={diffRevenue}
        diffPctRevenue={diffPctRevenue}
      />

      <AppointmentsChart
        lang={lang}
        t={t}
        loading={loading}
        data={data}
        yTicks={yTicks}
        yMax={yMax}
        employees={employees}
        hover={hover}
        setHover={setHover}
      />

      <PlanStatsChart
        lang={lang}
        t={t}
        loadingPlans={loadingPlans}
        planItems={planItems}
      />
    </div>
  );
}
